import React from 'react';
import { Tooltip } from 'antd';
import {
  useHasPermission,
  useHasAnyPermission,
  useHasAllPermissions,
} from '../../utils/permissionUtils';

/**
 * PolicySidebarButton — permission-gated sidebar button.
 * Uses Redux-connected hooks so the button reactively hides/shows
 * when permissions change (e.g. after login or role change).
 *
 * @param {string|string[]} permission
 * @param {boolean}         requireAll  – require ALL permissions (default: any)
 * @param {React.ReactNode} children
 * @param {boolean}         showTooltip – show label tooltip when collapsed
 * @param {string}          tooltipTitle
 * @param {object}          tooltipProps
 */
const tooltipStyles = {
  body: { fontSize: 12, padding: '6px 8px', minHeight: 'auto' },
};

const wrapButton = (node, extraProps = {}) =>
  React.cloneElement(node, {
    ...extraProps,
    style: { width: '100%', ...(node.props?.style || {}), ...(extraProps.style || {}) },
  });

const PolicySidebarButton = ({
  permission,
  requireAll = false,
  children,
  showTooltip = true,
  tooltipTitle,
  tooltipProps = {},
  ...buttonProps
}) => {
  const hasSingle  = useHasPermission(Array.isArray(permission) ? null : permission);
  const hasAnyPerm = useHasAnyPermission(Array.isArray(permission) ? permission : []);
  const hasAllPerms = useHasAllPermissions(Array.isArray(permission) ? permission : []);

  if (!permission) {
    if (showTooltip && tooltipTitle) {
      return (
        <Tooltip title={tooltipTitle} placement="right" styles={tooltipStyles} {...tooltipProps}>
          {wrapButton(children, buttonProps)}
        </Tooltip>
      );
    }
    return wrapButton(children, buttonProps);
  }

  const hasAccess = Array.isArray(permission)
    ? (requireAll ? hasAllPerms : hasAnyPerm)
    : hasSingle;

  if (!hasAccess) return null;

  const finalTooltipTitle = tooltipTitle || (typeof children === 'string' ? children : undefined);

  if (showTooltip && finalTooltipTitle) {
    return (
      <Tooltip title={finalTooltipTitle} placement="right" styles={tooltipStyles} {...tooltipProps}>
        {wrapButton(children, buttonProps)}
      </Tooltip>
    );
  }

  return wrapButton(children, buttonProps);
};

export default PolicySidebarButton;
